import { useMemo } from 'react';
import type { inferRouterOutputs } from '@trpc/server';
import { Link, useParams } from 'react-router-dom';
import { FaArrowLeft, FaColumns, FaExternalLinkAlt, FaFlask, FaRocket } from 'react-icons/fa';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Card } from '@/components/UI';
import StatusBadge, { type StatusTone } from '@/components/UI/StatusBadge';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc, trpcClient, trpcUnbatched } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useStrategyReadinessSummaries } from '@/hooks/useStrategyReadinessSummaries';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import type { AppRouter } from '@/types/appRouter';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type StrategySummary = RouterOutputs['strategy']['fetchStrategies']['strategies'][number];
type VariantMetadata = RouterOutputs['strategyVariant']['fetchFamily']['variants'][number]['metadata'];
type Scorecard = RouterOutputs['scorecards']['list'][number];
type AeroBinding = RouterOutputs['exitOptimization']['fetchExitSelectionBindings']['bindings'][number];
type AeroJob = RouterOutputs['exitOptimization']['fetchJobs']['jobs'][number];
type CoverageSummary = RouterOutputs['backtest']['getStrategiesCoverageSummary']['summaries'][number];
type ReadinessSummary = RouterOutputs['strategyReadiness']['getReadinessSummaryBatch']['summaries'][number];

interface ArtifactSnapshot {
    attachedExitCandidateCountByStrategyId: Partial<Record<number, number>>;
    aeroBindingsByStrategyId: Partial<Record<number, AeroBinding[]>>;
    scorecardsByStrategyId: Partial<Record<number, Scorecard[]>>;
}

interface VariantRow {
    activeAeroBindings: AeroBinding[];
    attachedExitCandidateCount: number;
    activeScorecard: Scorecard | null;
    coverage: CoverageSummary | null;
    latestAeroJob: AeroJob | null;
    metadata: VariantMetadata;
    readiness: ReadinessSummary | null;
    strategy: StrategySummary | null;
}

const EMPTY_ARTIFACT_SNAPSHOT: ArtifactSnapshot = {
    attachedExitCandidateCountByStrategyId: {},
    aeroBindingsByStrategyId: {},
    scorecardsByStrategyId: {}
};

const StrategyFamilyView = () => {
    const { familyId: familyIdParam } = useParams<{ familyId: string }>();
    const familyId = parsePositiveId(familyIdParam);

    const familyQuery = useAppQuery(
        {
            ...trpc.strategyVariant.fetchFamily.queryOptions({ familyId: familyId || 1 }),
            enabled: familyId > 0,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load strategy family',
            showErrorToast: true
        }
    );

    const familyStrategyIds = useMemo(() => {
        const ids = new Set<number>();

        for (const variant of familyQuery.data?.variants ?? []) {
            ids.add(variant.metadata.strategyId);
        }

        const rootStrategyId = familyQuery.data?.family.rootStrategyId ?? null;
        if (rootStrategyId !== null) ids.add(rootStrategyId);

        return Array.from(ids).sort((left, right) => left - right);
    }, [familyQuery.data]);

    const strategiesQuery = useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchStrategies.queryOptions({
                ids: familyStrategyIds,
                requireActive: false
            }),
            enabled: familyStrategyIds.length > 0,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load family strategies',
            showErrorToast: true
        }
    );

    const coverageQuery = useAppQuery(
        {
            ...trpc.backtest.getStrategiesCoverageSummary.queryOptions({
                strategyIds: familyStrategyIds
            }),
            enabled: familyStrategyIds.length > 0,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load backtest coverage',
            showErrorToast: true
        }
    );

    const readinessQuery = useStrategyReadinessSummaries(familyStrategyIds, {
        fallbackErrorMessage: 'Failed to load strategy readiness'
    });

    const aeroJobsQuery = useAppQuery(
        {
            ...trpc.exitOptimization.fetchJobs.queryOptions({}),
            enabled: familyStrategyIds.length > 0,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load AERO jobs',
            showErrorToast: true
        }
    );

    const artifactQuery = useAppQuery<ArtifactSnapshot, Error, ArtifactSnapshot, readonly ['strategy-family-artifacts', number, number[]]>(
        {
            queryKey: ['strategy-family-artifacts', familyId, familyStrategyIds] as const,
            queryFn: async (): Promise<ArtifactSnapshot> => fetchArtifactSnapshot(familyStrategyIds),
            enabled: familyStrategyIds.length > 0,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load linked strategy artifacts',
            showErrorToast: true
        }
    );

    const strategyById = useMemo(
        () => new Map((strategiesQuery.data?.strategies ?? []).map((strategy) => [strategy.id, strategy])),
        [strategiesQuery.data]
    );

    const coverageByStrategyId = useMemo(
        () => new Map((coverageQuery.data?.summaries ?? []).map((summary) => [summary.strategyId, summary])),
        [coverageQuery.data]
    );

    const readinessByStrategyId = useMemo(
        () => new Map((readinessQuery.data?.summaries ?? []).map((summary) => [summary.strategyId, summary])),
        [readinessQuery.data]
    );

    const latestAeroJobByStrategyId = useMemo(
        () => buildLatestAeroJobByStrategyId(aeroJobsQuery.data?.jobs ?? [], new Set(familyStrategyIds)),
        [aeroJobsQuery.data, familyStrategyIds]
    );

    const variantRows = useMemo(
        () =>
            buildVariantRows({
                artifacts: artifactQuery.data ?? EMPTY_ARTIFACT_SNAPSHOT,
                coverageByStrategyId,
                latestAeroJobByStrategyId,
                readinessByStrategyId,
                strategyById,
                variants: familyQuery.data?.variants.map((variant) => variant.metadata) ?? []
            }),
        [artifactQuery.data, coverageByStrategyId, familyQuery.data, latestAeroJobByStrategyId, readinessByStrategyId, strategyById]
    );

    const family = familyQuery.data?.family ?? null;
    const stats = buildFamilyStats(variantRows);
    const rootStrategyName = family?.rootStrategyId != null ? (strategyById.get(family.rootStrategyId)?.name ?? null) : null;

    if (familyId <= 0) {
        return (
            <UserLayout title="Strategy Family - Project 314">
                <div className="container mx-auto px-4 py-4">
                    <Card responsivePadding title="Invalid Strategy Family">
                        <p className="text-sm text-zinc-400">The strategy family route needs a positive family id.</p>
                    </Card>
                </div>
            </UserLayout>
        );
    }

    if (familyQuery.isLoading && !familyQuery.data) {
        return (
            <UserLayout title="Strategy Family - Project 314">
                <DataLoadingSpinner text="Loading strategy family..." />
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1600px" title={`${family?.name ?? 'Strategy Family'} - Project 314`}>
            <div className="container mx-auto space-y-4 px-4 py-4">
                <FamilyHeader familyId={familyId} familyName={family?.name ?? `Family #${familyId}`} />

                {familyQuery.data?.success === false ? (
                    <Card responsivePadding title="Unable to Load Family">
                        <p className="text-sm text-red-300">
                            {familyQuery.data.errorMessages.join(' ') || 'The family could not be loaded.'}
                        </p>
                    </Card>
                ) : (
                    <>
                        <FamilySummaryCards
                            familyDescription={family?.description ?? null}
                            familyId={familyId}
                            rootStrategyName={rootStrategyName}
                            stats={stats}
                        />
                        <VariantArtifactTable
                            areArtifactsLoading={artifactQuery.isLoading || coverageQuery.isLoading || readinessQuery.isLoading}
                            rows={variantRows}
                        />
                    </>
                )}
            </div>
        </UserLayout>
    );
};

const FamilyHeader = ({ familyId, familyName }: { familyId: number; familyName: string }) => (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
            <div className="mb-1 flex items-center gap-2 text-sm text-zinc-500">
                <Link className="inline-flex items-center gap-1 hover:text-zinc-300" to="/user/tradingStrategies">
                    <FaArrowLeft />
                    Strategies
                </Link>
                <span>/</span>
                <span className="text-zinc-300">{familyName}</span>
            </div>
            <h3 className="text-xl font-semibold text-white">{familyName}</h3>
        </div>
        <div className="flex flex-wrap gap-2">
            <Button icon={<FaColumns />} size="sm" to={`/user/strategyVariantComparisons?familyId=${familyId}`}>
                Compare variations
            </Button>
        </div>
    </div>
);

const FamilySummaryCards = ({
    familyDescription,
    familyId,
    rootStrategyName,
    stats
}: {
    familyDescription: string | null;
    familyId: number;
    rootStrategyName: string | null;
    stats: ReturnType<typeof buildFamilyStats>;
}) => (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.4fr_1fr]">
        <Card responsivePadding title="Family at a glance">
            <div className="space-y-4">
                {familyDescription && <p className="text-sm text-zinc-400">{familyDescription}</p>}
                <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                    <MetricBox label="Variations" value={String(stats.totalVariants)} />
                    <MetricBox label="Trading" value={String(stats.activeStrategies)} />
                    <MetricBox label="Inactive" value={String(stats.inactiveStrategies)} />
                </div>
            </div>
        </Card>
        <Card responsivePadding title="Lineage & comparison">
            <div className="space-y-3">
                <div>
                    <div className="text-xs text-zinc-500">Base strategy</div>
                    <div className="mt-0.5 text-sm font-medium text-zinc-100">{rootStrategyName ?? 'None'}</div>
                </div>
                <div>
                    <div className="text-xs text-zinc-500">Variation comparison</div>
                    <Link
                        className="mt-1 inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
                        to={`/user/strategyVariantComparisons?familyId=${familyId}`}
                    >
                        Compare variations using existing backtests
                    </Link>
                </div>
            </div>
        </Card>
    </div>
);

const VariantArtifactTable = ({ areArtifactsLoading, rows }: { areArtifactsLoading: boolean; rows: VariantRow[] }) => (
    <Card
        responsivePadding
        headerActions={
            areArtifactsLoading ? (
                <StatusBadge pulse status="loading" tone="info">
                    Refreshing
                </StatusBadge>
            ) : (
                <StatusBadge status="current" tone="neutral">
                    Up to date
                </StatusBadge>
            )
        }
        title="Variations and what they use"
    >
        {rows.length === 0 ? (
            <div className="rounded border border-zinc-800 bg-zinc-950 px-4 py-6 text-sm text-zinc-400">
                No variations in this family yet.
            </div>
        ) : (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-zinc-800 text-sm">
                    <thead className="bg-zinc-950">
                        <tr className="text-left text-xs uppercase tracking-wide text-zinc-400">
                            <th className="px-3 py-2">Variation</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Entry / exit logic</th>
                            <th className="px-3 py-2">Screener</th>
                            <th className="px-3 py-2">Scorecard</th>
                            <th className="px-3 py-2">Exit rules</th>
                            <th className="px-3 py-2">AERO</th>
                            <th className="px-3 py-2">Backtests</th>
                            <th className="px-3 py-2">Champion</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                        {rows.map((row) => (
                            <VariantArtifactRow key={row.metadata.strategyId} row={row} />
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </Card>
);

const VariantArtifactRow = ({ row }: { row: VariantRow }) => {
    const { metadata, strategy } = row;
    const strategyName = strategy?.name ?? metadata.variantLabel;

    return (
        <tr className="align-top hover:bg-zinc-900/70">
            <td className="px-3 py-3">
                <Link
                    className="font-medium text-blue-400 underline-offset-2 hover:text-blue-300 hover:underline"
                    to={`/user/tradingStrategy/${metadata.strategyId}`}
                >
                    {strategyName}
                </Link>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                    {metadata.sourceStrategyId === null ? (
                        <StatusBadge status="base" tone="accent">
                            Base
                        </StatusBadge>
                    ) : null}
                    {metadata.variantLabel !== strategyName && <span className="text-xs text-zinc-500">{metadata.variantLabel}</span>}
                </div>
            </td>
            <td className="px-3 py-3">
                <div className="flex flex-col items-start gap-1">
                    {strategy && (
                        <StatusBadge status={strategy.active ? 'trading' : 'paused'} tone={strategy.active ? 'success' : 'neutral'}>
                            {strategy.active ? 'Trading' : 'Paused'}
                        </StatusBadge>
                    )}
                    {strategy?.autoBacktest && (
                        <StatusBadge status="auto" tone="info">
                            Auto-backtest
                        </StatusBadge>
                    )}
                </div>
            </td>
            <td className="px-3 py-3">
                <ArtifactId label="Entry" value={strategy?.tradeLogicV2EntryVersionId ?? null} />
                <ArtifactId label="Exit" value={strategy?.tradeLogicV2ExitVersionId ?? null} />
            </td>
            <td className="px-3 py-3">
                <ScreenerState screenerRuleId={strategy?.activeScreenerRuleId ?? null} />
            </td>
            <td className="px-3 py-3">
                <ScorecardState activeScorecard={row.activeScorecard} />
            </td>
            <td className="px-3 py-3">
                <StatusBadge
                    status={row.attachedExitCandidateCount > 0 ? 'attached' : 'none'}
                    tone={row.attachedExitCandidateCount > 0 ? 'success' : 'neutral'}
                >
                    {row.attachedExitCandidateCount > 0 ? `${row.attachedExitCandidateCount} attached` : 'None'}
                </StatusBadge>
            </td>
            <td className="px-3 py-3">
                <AeroState activeBindings={row.activeAeroBindings} latestJob={row.latestAeroJob} />
            </td>
            <td className="px-3 py-3">
                <BacktestState coverage={row.coverage} readiness={row.readiness} strategyId={metadata.strategyId} />
            </td>
            <td className="px-3 py-3">
                <ExperimentState strategy={strategy} />
            </td>
            <td className="px-3 py-3">
                <div className="flex justify-end gap-2">
                    <Button
                        icon={<FaExternalLinkAlt />}
                        size="sm"
                        to={`/user/tradingStrategy/${metadata.strategyId}`}
                        variant="outline-dark"
                    >
                        Open
                    </Button>
                    <Button icon={<FaRocket />} size="sm" to={`/user/aero/new?strategyId=${metadata.strategyId}`} variant="outline-dark">
                        AERO
                    </Button>
                </div>
            </td>
        </tr>
    );
};

async function fetchArtifactSnapshot(strategyIds: number[]): Promise<ArtifactSnapshot> {
    const [scorecards, strategiesResult, exitCandidateResults, aeroBindingResults] = await Promise.all([
        trpcClient.scorecards.list.query(),
        trpcClient.strategy.fetchStrategies.query({ requireActive: false, ids: strategyIds }),
        Promise.all(
            strategyIds.map((strategyId) =>
                trpcClient.tradeLogicExitCandidates.getStrategyCandidateAttachments.query({
                    strategyId
                })
            )
        ),
        Promise.all(
            strategyIds.map((strategyId) =>
                trpcClient.exitOptimization.fetchExitSelectionBindings.query({
                    includeRetired: false,
                    strategyId
                })
            )
        )
    ]);

    const snapshot: ArtifactSnapshot = {
        attachedExitCandidateCountByStrategyId: {},
        aeroBindingsByStrategyId: {},
        scorecardsByStrategyId: {}
    };
    const scorecardById = new Map(scorecards.map((scorecard) => [scorecard.id, scorecard]));
    const strategyById = new Map(strategiesResult.strategies.map((strategy) => [strategy.id, strategy]));

    strategyIds.forEach((strategyId, index) => {
        const activeScorecardId = strategyById.get(strategyId)?.activeScorecardId ?? null;
        const activeScorecard = activeScorecardId === null ? null : (scorecardById.get(activeScorecardId) ?? null);
        snapshot.scorecardsByStrategyId[strategyId] = activeScorecard ? [activeScorecard] : [];
        snapshot.attachedExitCandidateCountByStrategyId[strategyId] = exitCandidateResults[index]?.candidates.length ?? 0;

        snapshot.aeroBindingsByStrategyId[strategyId] =
            aeroBindingResults[index]?.bindings.filter((binding) => binding.status === 'active') ?? [];
    });

    return snapshot;
}

function buildVariantRows(params: {
    artifacts: ArtifactSnapshot;
    coverageByStrategyId: Map<number, CoverageSummary>;
    latestAeroJobByStrategyId: Map<number, AeroJob>;
    readinessByStrategyId: Map<number, ReadinessSummary>;
    strategyById: Map<number, StrategySummary>;
    variants: VariantMetadata[];
}): VariantRow[] {
    return params.variants
        .map((metadata) => {
            const { strategyId } = metadata;
            const scorecards = params.artifacts.scorecardsByStrategyId[strategyId] ?? [];

            return {
                activeAeroBindings: params.artifacts.aeroBindingsByStrategyId[strategyId] ?? [],
                attachedExitCandidateCount: params.artifacts.attachedExitCandidateCountByStrategyId[strategyId] ?? 0,
                activeScorecard: scorecards[0] ?? null,
                coverage: params.coverageByStrategyId.get(strategyId) ?? null,
                latestAeroJob: params.latestAeroJobByStrategyId.get(strategyId) ?? null,
                metadata,
                readiness: params.readinessByStrategyId.get(strategyId) ?? null,
                strategy: params.strategyById.get(strategyId) ?? null
            };
        })
        .sort(compareVariantRows);
}

// The root stays pinned at the top; every other variation sorts newest first, matching the strategy list. Status is
// already a badge on each row, so grouping by it would only reshuffle the order whenever a strategy is paused or resumed.
function compareVariantRows(left: VariantRow, right: VariantRow): number {
    const roleDelta = variantSortWeight(left.metadata) - variantSortWeight(right.metadata);
    if (roleDelta !== 0) return roleDelta;

    return right.metadata.strategyId - left.metadata.strategyId;
}

function variantSortWeight(metadata: VariantMetadata): number {
    return metadata.sourceStrategyId === null ? 0 : 1;
}

function buildLatestAeroJobByStrategyId(jobs: AeroJob[], strategyIds: Set<number>): Map<number, AeroJob> {
    const latestByStrategyId = new Map<number, AeroJob>();

    for (const job of jobs) {
        if (!strategyIds.has(job.strategyId)) continue;

        const current = latestByStrategyId.get(job.strategyId);

        if (!current || getSortableTime(job.createdAt) > getSortableTime(current.createdAt)) {
            latestByStrategyId.set(job.strategyId, job);
        }
    }

    return latestByStrategyId;
}

function buildFamilyStats(rows: VariantRow[]) {
    return {
        activeStrategies: rows.filter((row) => row.strategy?.active).length,
        inactiveStrategies: rows.filter((row) => !row.strategy?.active).length,
        totalVariants: rows.length
    };
}

const MetricBox = ({ label, value }: { label: string; value: string }) => (
    <div className="rounded border border-zinc-800 bg-zinc-950 px-3 py-2">
        <div className="text-xs text-zinc-500">{label}</div>
        <div className="text-lg font-semibold text-zinc-100">{value}</div>
    </div>
);

const ArtifactId = ({ label, value }: { label: string; value: number | null }) => (
    <div className="text-xs text-zinc-300">
        <span className="mr-1 text-zinc-500">{label}</span>
        {value === null ? <span className="text-zinc-500">None</span> : <span className="font-mono">v{value}</span>}
    </div>
);

const ScreenerState = ({ screenerRuleId }: { screenerRuleId: number | null }) => {
    if (screenerRuleId === null) {
        return (
            <StatusBadge status="none" tone="neutral">
                None
            </StatusBadge>
        );
    }

    return (
        <StatusBadge status="attached" tone="success">
            #{screenerRuleId}
        </StatusBadge>
    );
};

const ScorecardState = ({ activeScorecard }: { activeScorecard: Scorecard | null }) => {
    if (!activeScorecard) {
        return (
            <StatusBadge status="none" tone="neutral">
                None
            </StatusBadge>
        );
    }

    return (
        <div className="space-y-1">
            <StatusBadge status={activeScorecard.initializationStatus} />
            <div className="max-w-[180px] truncate text-xs text-zinc-300">{activeScorecard.name}</div>
        </div>
    );
};

const AeroState = ({ activeBindings, latestJob }: { activeBindings: AeroBinding[]; latestJob: AeroJob | null }) => (
    <div className="space-y-1">
        <StatusBadge status={activeBindings.length > 0 ? 'active' : 'none'} tone={activeBindings.length > 0 ? 'success' : 'neutral'}>
            {activeBindings.length > 0 ? `${activeBindings.length} active binding${activeBindings.length === 1 ? '' : 's'}` : 'No binding'}
        </StatusBadge>
        {latestJob && (
            <div className="flex flex-col items-start gap-1">
                <StatusBadge status={latestJob.status} />
                <Link className="text-xs text-blue-400 hover:text-blue-300" to={`/user/aero/${latestJob.id}`}>
                    View AERO job
                </Link>
            </div>
        )}
    </div>
);

const BacktestState = ({
    coverage,
    readiness,
    strategyId
}: {
    coverage: CoverageSummary | null;
    readiness: ReadinessSummary | null;
    strategyId: number;
}) => (
    <div className="space-y-1">
        {coverage ? (
            <Link
                className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                to={`/user/tradingStrategy/${strategyId}?section=backtests`}
            >
                {formatCoverage(coverage)}
            </Link>
        ) : (
            <StatusBadge status="unknown" tone="neutral">
                Unknown
            </StatusBadge>
        )}
        {readiness && (
            <div>
                <StatusBadge status={readiness.overallStatus} tone={readinessTone(readiness.overallStatus)}>
                    {readiness.checklistCompleted}/{readiness.checklistTotal} {formatReadinessStatus(readiness.overallStatus)}
                </StatusBadge>
            </div>
        )}
    </div>
);

const ExperimentState = ({ strategy }: { strategy: StrategySummary | null }) => {
    if (!strategy) {
        return (
            <StatusBadge status="unknown" tone="neutral">
                Unknown
            </StatusBadge>
        );
    }

    if (!strategy.currentChampionExperimentId) {
        return (
            <Link
                className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                to={`/user/experiments?strategyId=${strategy.id}`}
            >
                <FaFlask />
                Open experiments
            </Link>
        );
    }

    return (
        <div className="space-y-1">
            <Link
                className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                to={`/user/experimentResults/${strategy.currentChampionExperimentId}?strategyId=${strategy.id}`}
            >
                <FaFlask />
                View champion
            </Link>
            {strategy.currentChampionPromotedAt && (
                <div className="text-xs text-zinc-500">Promoted {formatDateTime(strategy.currentChampionPromotedAt)}</div>
            )}
        </div>
    );
};

function parsePositiveId(value: string | undefined): number {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
}

function getSortableTime(value: string | null): number {
    if (!value) return 0;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function formatCoverage(summary: CoverageSummary): string {
    if (summary.totalEntitiesInUniverse === 0) return 'No universe';
    if (summary.entitiesWithBacktestsCount === 0) return 'No backtests';

    if (summary.entitiesWithBacktestsCount === summary.totalEntitiesInUniverse) {
        return `Complete ${summary.entitiesWithBacktestsCount}/${summary.totalEntitiesInUniverse}`;
    }

    return `Partial ${summary.entitiesWithBacktestsCount}/${summary.totalEntitiesInUniverse}`;
}

function formatDateTime(value: string): string {
    return value.replace('T', ' ').slice(0, 16);
}

function readinessTone(status: ReadinessSummary['overallStatus']): StatusTone {
    if (status === 'ready') return 'success';
    if (status === 'needs_attention') return 'warning';
    if (status === 'not_ready') return 'danger';

    return 'neutral';
}

function formatReadinessStatus(status: ReadinessSummary['overallStatus']): string {
    if (status === 'ready') return 'Ready';
    if (status === 'needs_attention') return 'Needs attention';
    if (status === 'not_ready') return 'Not ready';

    return status;
}

export default StrategyFamilyView;
